import { useState } from "react";
import useDataset from "datasets/hooks/useDataset";
import useDatasetPermission from "datasets/hooks/useDatasetPermission";
import useStatistics from "datasets/hooks/useStatistics";
import { DatasetAccessLevel } from "datasets/types/enums/access-level";
import Dataset from "datasets/types/interfaces/dataset";
import { formatDistanceToNowStrict } from "date-fns";
import prettyBytes from "pretty-bytes";
import { Skeleton } from "~/components/ui/skeleton";
import useToast from "~/components/ui/toast";
import { useFavorites } from "~/hooks/useFavorites";
import { AvatarStack } from "~/pages/manifold-nextgen/components/AvatarStack";
import { EditableDescription } from "~/pages/manifold-nextgen/components/EditableDescription";
import { EditableIcon } from "~/pages/manifold-nextgen/components/EditableIcon";
import { EditableTitle } from "~/pages/manifold-nextgen/components/EditableTitle";
import {
  EntityOverviewHeader,
  OverviewTile,
} from "~/pages/manifold-nextgen/components/EntityOverviewHeader";
import { FavoriteButton } from "~/pages/manifold-nextgen/components/FavoriteButton";
import { AccessLevelBadge } from "~/pages/manifold-nextgen/components/datasets/AccessLevelBadge";
import { DatasetAppearanceDialog } from "~/pages/manifold-nextgen/components/datasets/DatasetAppearanceDialog";
import { DatasetIcon } from "~/pages/manifold-nextgen/components/datasets/DatasetIcon";
import { useDatasetShare } from "~/pages/manifold-nextgen/hooks/useDatasetShare";
import { useOwners } from "~/pages/manifold-nextgen/hooks/useOwners";
import { apiMutate } from "~/utils/apiClient";

const { VITE_CATALOG_API } = import.meta.env;

interface DatasetOverviewProps {
  // Overrides the route-fetched dataset — used when this tab is rendered
  // outside the DatasetDetail route tree (the project context sidebar).
  dataset?: Dataset;
  // True when this tab is rendered inside the session context sidebar,
  // which only ever shows a read-only view.
  sessionSidebarView?: boolean;
}

export const DatasetOverview = ({
  dataset: datasetProp,
  sessionSidebarView = false,
}: DatasetOverviewProps = {}) => {
  const { data: fetchedDataset, isLoading, mutate } = useDataset(datasetProp?.id);
  const dataset = datasetProp ?? fetchedDataset;
  const { data: stats, isLoading: loadingStats } = useStatistics(dataset?.id);
  const hasEditPermission = useDatasetPermission("edit:catalog-dataset", dataset?.id);
  const canEdit =
    !sessionSidebarView && dataset?.access_level === DatasetAccessLevel.OWNER && hasEditPermission;
  const canCustomizeAppearance =
    !sessionSidebarView && dataset?.access_level === DatasetAccessLevel.OWNER;
  const [appearanceOpen, setAppearanceOpen] = useState(false);
  const { collaborators, accessLevels } = useDatasetShare(dataset?.id, !sessionSidebarView);
  const { showError } = useToast();
  const { isFavorite, toggleFavorite } = useFavorites(
    "dataset-favorites",
    dataset ? [dataset] : [],
    (d) => d.id
  );

  const handleRename = async (next: string) => {
    if (!dataset) {
      return;
    }
    try {
      await apiMutate<Dataset>(`${VITE_CATALOG_API}/datasets/${dataset.id}`, "PATCH", {
        title: next,
      });
      await mutate();
    } catch (e) {
      showError("Failed to rename dataset");
      throw e;
    }
  };

  const handleSaveDescription = async (next: string) => {
    if (!dataset) {
      return;
    }
    try {
      await apiMutate<Dataset>(`${VITE_CATALOG_API}/datasets/${dataset.id}`, "PATCH", {
        description: next,
      });
      await mutate();
    } catch (e) {
      showError("Failed to update dataset description");
      throw e;
    }
  };

  const owners = useOwners(collaborators, accessLevels, DatasetAccessLevel.OWNER);

  if (!datasetProp && isLoading) {
    return (
      <div
        role="status"
        aria-label="Loading overview"
        className="mx-auto flex w-full max-w-3xl flex-col gap-4"
      >
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-6 w-72" />
        <Skeleton className="h-24 w-full" />
      </div>
    );
  }

  if (!dataset) return null;

  const tiles: OverviewTile[] = [
    {
      label: "My Role",
      content: <AccessLevelBadge accessLevel={dataset.access_level} />,
    },
    ...(sessionSidebarView
      ? []
      : [
          {
            label: "Owners",
            content: <AvatarStack items={owners} />,
          },
        ]),
    {
      label: "Data Size",
      content: loadingStats ? (
        <Skeleton className="h-4 w-16" />
      ) : stats?.table_total_bytes !== undefined ? (
        prettyBytes(stats.table_total_bytes)
      ) : (
        "—"
      ),
    },
    {
      label: "Last Updated",
      content: dataset.updated_at
        ? formatDistanceToNowStrict(new Date(dataset.updated_at), {
            addSuffix: true,
          })
        : "—",
    },
  ];

  return (
    <div className="mx-auto w-full max-w-3xl pb-12">
      <EntityOverviewHeader
        tiles={tiles}
        title={
          sessionSidebarView ? undefined : (
            <div className="flex items-center gap-3">
              <EditableIcon
                icon={<DatasetIcon dataset={dataset} size={36} />}
                canEdit={canCustomizeAppearance}
                onEdit={() => setAppearanceOpen(true)}
              />
              <EditableTitle
                value={dataset.title}
                canEdit={canEdit}
                label="dataset name"
                onSave={handleRename}
              />
              <FavoriteButton
                favorite={isFavorite(dataset.id)}
                onToggle={() => toggleFavorite(dataset.id)}
              />
            </div>
          )
        }
      >
        <EditableDescription
          value={dataset.description}
          canEdit={canEdit}
          onSave={handleSaveDescription}
        />
      </EntityOverviewHeader>
      {canCustomizeAppearance && (
        <DatasetAppearanceDialog
          dataset={dataset}
          open={appearanceOpen}
          onOpenChange={setAppearanceOpen}
        />
      )}
    </div>
  );
};
